<script lang="ts" setup>
  import { computed, getCurrentInstance, ref } from 'vue'

  import SpinnerDefault from '@/elements/SpinnerDefault.vue'
  import { Router } from '@/helpers/router'
  import { type SearchResult, type SearchResultEffortGroups, useGlobalSearchStore } from '@/stores'
  import { ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/solid'
  import { debounce } from 'lodash-es'
  import type { EffortJsonldVueGlobalSearchDefaultReadSync, GlobalSearchJsonldGlobalSearch } from '@/api'

  import Menu from 'primevue/menu'
  import HeaderSearchResultList from '@/components/global-search/HeaderSearchResultList.vue'
  import ButtonRouter from '@/elements/ButtonRouter.vue'
  import CheckboxTitrop from '@/elements/CheckboxTitrop.vue'
  import { useI18n } from 'vue-i18n'
  import { watchDebounced } from '@vueuse/core'
  import type { MenuItem } from 'primevue/menuitem'
  import { useCommandSearchService } from '@/services/CommandSearchService.ts'
  import { useTitropEvents } from '@/composables/useTitropEvents.ts'
  import { UserIcon } from '@/config/icons.ts'
  import ButtonDefault from '@/elements/ButtonDefault.vue'

  const { t } = useI18n()

  const scopeIcons = {
    'Everywhere': 'SearchAllIcon',
    'Business partners': 'PartnerIcon',
    'Orders': 'OrderIcon',
    'Service calls': 'ServiceCallIcon',
    'Mitarbeiter': 'UserIcon',
    'Modul': 'ModuleIcon',
    'Infoboard': 'InfoboardOverviewIcon',
    'Anfragenverwaltung': 'RequestIcon',
    'Auftrag': 'OrderAltIcon'
  }

  const supportedScopes = ref<string[]>(['Everywhere', 'Business partners', 'Orders', 'Service calls'])

  const globalSearchStore = useGlobalSearchStore()
  const scopeList = globalSearchStore.getScopes()

  const dropdownArrow = ref()
  const resultPending = ref(false)
  const searchResult = ref<SearchResultEffortGroups>()
  const showResultList = ref(false)
  const searchQuery = ref<string>('')
  type AllScopesType = keyof Required<GlobalSearchJsonldGlobalSearch>['results']
  const searchScope = ref<AllScopesType | undefined>()
  const searchIncludeClosed = ref(false)
  const searchDebounced = debounce(doSearch, 800)

  const instance = getCurrentInstance()

  const selectedScopeName = computed(() => {
    const selectedScope = scopeList.find((d) => d.key === (searchScope.value ?? ''))
    if (!selectedScope) return '(invalid value)'
    return t(selectedScope.value)
  })

  async function handleSpecialSearch(input: string) {
    await useCommandSearchService().handle(input)
  }

  async function doSearch() {
    if (!searchQuery.value || searchQuery.value.trim().length < 3) {
      return
    }
    resultPending.value = true

    //instance?.proxy?.$forceUpdate()
    try {
      if (searchQuery.value.startsWith('/')) {
        //special functions
        await handleSpecialSearch(searchQuery.value)
      } else {
        const results: SearchResult = await globalSearchStore.search(searchQuery.value, searchScope.value ? [searchScope.value] : undefined, searchIncludeClosed.value)
        renderResults(results)
      }
    } catch {
      debugger
    } finally {
      resultPending.value = false
    }
  }

  function renderResults(results: SearchResult) {
    searchResult.value = results.results
    showResultList.value = true
  }

  function setScope(scope: AllScopesType | '') {
    if (scope) {
      searchScope.value = scope
    } else searchScope.value = undefined
    closeScopeMenu()

    void searchDebounced()
  }

  /*function onEnterPressed(event: KeyboardEvent) {
    if (event.key === 'Enter' || event.code === 'Enter') {
      void searchDebounced()
    }
  }*/

  function onShortcutClicked() {
    //useEventBus('content:show').emit()
    showResultList.value = false
  }

  function onResultSelected(effort: EffortJsonldVueGlobalSearchDefaultReadSync) {
    //useEventBus('content:show').emit()
    showResultList.value = false

    void Router.push({
      name: 'effort_details',
      params: { id: effort.id }
    })
  }

  const scopeMenuOpen = ref(false)
  const scopeMenu = ref<InstanceType<typeof Menu>>()

  function toggleScopeMenu(event: Event) {
    scopeMenu.value?.toggle(event)
    scopeMenuOpen.value = !scopeMenuOpen.value
  }

  const onMenuHide = () => {
    scopeMenuOpen.value = false
  }

  function closeScopeMenu() {
    scopeMenu.value?.hide()
    scopeMenuOpen.value = false
  }

  const scopeMenuItems = computed<MenuItem[]>(() => {
    return scopeList.map(
      (s: { key: AllScopesType | ''; value: string }): MenuItem => ({
        label: s.value,
        click: () => {
          setScope(s.key)
        }
      })
    )
  }) // <MenuItem

  function onOverlayShow() {
    //useEventBus('content:hide').emit()
  }

  const lockListClose = ref(false)

  function onOverlayHide() {
    if (lockListClose.value) return
    //useEventBus('content:show').emit()
    showResultList.value = false
  }

  const frmRef = ref()
  const searchResultOverlay = ref<InstanceType<typeof HeaderSearchResultList>>()
  const searchField = ref<HTMLInputElement>()
  useTitropEvents().hotkeyFocusSearch.on(() => {
    if (!searchField.value) return

    searchResultOverlay.value?.hide()
    searchField.value.focus()
    searchField.value.selectionStart = 0
    searchField.value.selectionEnd = searchField.value.value.length
  })

  useTitropEvents().hotkeyChangeSearchScope.on((_: unknown, payload: { scope: number }) => {
    if (scopeMenuItems.value[payload.scope]) scopeMenuItems.value[payload.scope].click(new Event('dummy'))
  })

  watchDebounced(
    searchQuery,
    () => {
      void doSearch()
    },
    { debounce: 800 }
  )

  const lastSearchEvent = ref<Event | undefined>(undefined)

  function rememberSearchEvent(event: Event) {
    lastSearchEvent.value = event
  }
</script>

<template>
  <div>
    <!--relative flex flex-1 items-center sm:pl-8-->
    <form

      class="relative flex flex-1 items-center"
      method="GET"
      @submit="$event.preventDefault()">
      <div ref="frmRef" class="relative flex flex-1 border border-slate-200 dark:border-gray-700 rounded-md" id="globalSearchField">
        <!-- eslint-disable-next-line titrop/no-standard-html-elements -->
        <button
          id="dropdown-button"
          :class="[
            'flex-shrink-0 inline-flex items-center py-2.5 px-3 text-sm font-medium text-center border-r border-slate-200 hover:bg-slate-50 dark:border-gray-700 dark:hover:bg-gray-800 focus:ring-4 focus:outline-none focus:ring-gray-100 dark:focus:ring-gray-900 h-full'
          ]"
          :disabled="resultPending"
          data-dropdown-toggle="dropdown"
          type="button"
          @click="toggleScopeMenu">
          {{ selectedScopeName }}
          <ChevronDownIcon
            ref="dropdownArrow"
            :class="{ 'rotate-180': scopeMenuOpen }"
            aria-hidden="true"
            class="text-gray-400 dark:text-gray-600 ml-2 mr-0 h-5 w-5 shrink-0" />
        </button>
        <Menu
          ref="scopeMenu"
          :model="scopeMenuItems"
          :popup="true"
          class="text-sm"
          @hide="onMenuHide">
          <template #item="{ item }">
            <div class>
              <!-- eslint-disable-next-line titrop/no-standard-html-elements -->
              <!--              <button
                              v-ripple
                              class="inline-flex w-full px-2 py-2 titrop-button-secondary items-center"
                              @click="item.click"
                              :data-p-disabled="!supportedScopes.includes(item.label as string)"
                              :disabled="!supportedScopes.includes(item.label as string)">
                              <component :is="scopeIcons[item.label as string]" class="mr-1 size-6" />
                              {{ t(typeof item.label == 'function' ? item.label() : (item.label ?? 'MISSING LABEL')) }}
                            </button>-->

              <ButtonDefault
                class="w-full justify-start text-gray-700"
                size="small"
                variant="text"
                severity="secondary"
                :icon="scopeIcons[item.label as string]"
                :label="t(typeof item.label == 'function' ? item.label() : (item.label ?? 'MISSING LABEL'))"
                :disabled="!supportedScopes.includes(item.label as string)"
                @click="item.click" />
            </div>
          </template>
          <template #end>
            <Divider type="dashed" class="my-2" />
            <div class="flex items-center w-full px-2 pb-2 text-sm">
              <CheckboxTitrop
                v-model="searchIncludeClosed"
                :binary="true"
                input-id="includeClosed" />
              <label
                class="ml-2"
                for="includeClosed"
                @click="searchIncludeClosed = !searchIncludeClosed">
                auch geschlossen
              </label>
            </div>
            <div class="flex align-items-center w-full px-2 pb-2 text-sm">
              <ButtonRouter
                :to="{ name: 'filter-builder' }"
                class="w-full"
                label="Erweiterte Suche"
                size="small"
                outlined
                @click="closeScopeMenu" />
            </div>
          </template>
        </Menu>

        <div :class="['relative w-full px-1']">
          <label
            class="sr-only"
            for="search-field">
            Suche...
          </label>
          <MagnifyingGlassIcon
            aria-hidden="true"
            class="pointer-events-none absolute inset-y-0 left-2 h-full w-5 text-gray-400 dark:text-gray-600" />

          <!-- eslint-disable-next-line titrop/no-standard-html-elements -->
          <input
            id="search-field"
            ref="searchField"
            v-model="searchQuery"
            :class="['bg-white dark:bg-black', 'block h-full w-full border-0 py-0 pl-8 pr-0  focus:ring-0 sm:text-sm']"
            autocomplete="off"
            placeholder="Suche..."
            type="search"
            value=""
            @focus="rememberSearchEvent"
            @click="rememberSearchEvent" />
          <SpinnerDefault
            v-if="resultPending"
            class="absolute inset-y-0 right-2 h-full w-5" />
        </div>
      </div>

      <HeaderSearchResultList
        v-if="showResultList"
        ref="searchResultOverlay"
        v-model:lock="lockListClose"
        :results="searchResult"
        :searched-scope="searchScope"
        :target="frmRef"
        :event="lastSearchEvent"
        :pop-up-class="`p-0 mx-auto w-full md:w-11/12 md:max-h-[93%] overflow-y-auto`"
        @result-selected="onResultSelected"
        @shortcut-clicked="onShortcutClicked"
        @overlay-show="onOverlayShow"
        @overlay-hide="onOverlayHide" />
    </form>
  </div>
</template>

<i18n>
{
  "de": {
    "Everywhere": "Überall",
    "Business partners": "Geschäftspartner",
    "Orders": "Auftragszeilen",
    "Service calls": "Service-Calls"
  },
  "en": {
    "Everywhere": "Everywhere",
    "Business partners": "Business partners",
    "Orders": "Orders",
    "Service calls": "Service-Calls"
  }
}
</i18n>
